import { CheckIcon } from "@chakra-ui/icons";
import { useEffect, useState } from "react";
import { MdEdit, MdInfo } from "react-icons/md";
import { Box, IconButton, Input, HStack, Text, useToast, Tooltip, VStack, Textarea } from "@chakra-ui/react";
import { FaSave, FaShareAlt } from "react-icons/fa";
import showToast from "../../hooks/useCustomToast";
import { useCommentaryContext } from "../../context/CommentaryContext";

interface TreeNameProps {
  treeName: string;
  treeDescription: string;
  handleTreeNameAndDescription: (name: string, description: string) => void;
  isChatOpen?: boolean;
  handleSaveTree?: () => Promise<void>;
}

/*
 * This component renders a text input field that allows the user to edit a header.
 * When the user clicks on the text, it switches to an editable input field.
 * When the user finishes editing (e.g., by clicking outside the input field),
 * the value is saved and the input field switches back to display mode.
 */

const TreeName: React.FC<TreeNameProps> = ({ 
  treeName, 
  treeDescription,
  handleTreeNameAndDescription, 
  isChatOpen = false,
  handleSaveTree
}) => {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [value, setValue] = useState<string>(treeName);
  const [treeDescriptionValue, setTreeDescriptionValue] = useState<string>(treeDescription);
  const toast = useToast();
  const { hasAnyLoading } = useCommentaryContext();

  useEffect(() => {
    setValue(treeName);
    setTreeDescriptionValue(treeDescription);
  }, [treeName, treeDescription]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  // Handles saving the tree name and description.
  const handleSave = () => {
    if (!value.trim()) {
      showToast("Error", "Tree Name can't be empty", "error");
      return;
    }
    
    // Check if values have actually changed or not.
    if (treeName.trim() === value.trim() && treeDescription.trim() === treeDescriptionValue.trim()) {
      // Reset to original values since no meaningful change occurred.
      setValue(treeName);
      setTreeDescriptionValue(treeDescription);
      setIsEditing(false);
      return;
    }
    
    setIsEditing(false);
    handleTreeNameAndDescription(value, treeDescriptionValue.trim());
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setTreeDescriptionValue(e.target.value);
  };

  // function to copy the current URL to the clipboard
  const handleShare = () => {
    const currentUrl = window.location.href;
    navigator.clipboard.writeText(currentUrl).then(() => {
      toast({
        title: "URL copied!",
        description: "The link has been copied to your clipboard",
        status: "success",
        duration: 2000,
        isClosable: true,
        position: "top"
      });
    }).catch((err) => {
      toast({
        title: "Failed to copy",
        description: "Could not copy the URL to clipboard",
        status: "error",
        duration: 2000,
        isClosable: true,
        position: "top"
      });
    });
  };

  return (
    <Box w="100%">
      <HStack w="100%">
        {isEditing ? (
          <HStack w="100%" spacing={2} align="flex-start">
            <VStack w="100%" spacing={2} align="stretch">
              <Input
                type="text"
                value={value}
                onChange={handleNameChange}
                placeholder="Tree Name"
                autoFocus
                p={2}
                size={isChatOpen ? "xs" : "sm"}
                fontSize={isChatOpen ? "xs" : "sm"}
              />
              <Textarea
                value={treeDescriptionValue}
                onChange={handleDescriptionChange}
                placeholder="Tree Description"
                size={isChatOpen ? "xs" : "sm"}
                fontSize={isChatOpen ? "xs" : "sm"}
                resize="none"
                rows={2}
              />
            </VStack>
            <CheckIcon 
              onClick={handleSave} 
              cursor="pointer" 
              boxSize={isChatOpen ? 4 : 5} 
              color="green.500"
              mt={2}
            />
          </HStack>
        ) : (
          <HStack justifyContent="space-between" w="100%">
            <HStack spacing={1}>
              <Text 
                mb={0} 
                fontSize={isChatOpen ? "sm" : "md"} 
                fontWeight={500} 
                color="gray.700"
              >
                {value}
              </Text>
              <Tooltip 
                label={treeDescriptionValue || "Description is empty."}
                placement="right"
                hasArrow
              >
                <Box>
                  <IconButton
                    as={MdInfo}
                    boxSize={isChatOpen ? 4 : 5}
                    size="xs"
                    _hover={{ bg: "gray.100", color: "gray.600" }}
                    aria-label="Description"
                    cursor="pointer"
                    variant="ghost"
                    colorScheme="gray"
                    color="gray.400"
                  />
                </Box>
              </Tooltip>
            </HStack>
            <HStack spacing={0}>
              <IconButton
                as={MdEdit}
                onClick={handleEdit}
                boxSize={isChatOpen ? 4 : 5}
                size="xs"
                _hover={{ bg: "gray.100", color: "gray.600" }}
                aria-label="Edit"
                cursor="pointer"
                variant="ghost"
                colorScheme="gray"
                color="gray.400"
                isDisabled={hasAnyLoading}
              />
              <IconButton
                as={FaSave}
                onClick={handleSaveTree}
                boxSize={isChatOpen ? 4 : 5}
                size="xs"
                _hover={{ bg: "gray.100", color: "gray.600" }}
                aria-label="Save"
                cursor="pointer"
                variant="ghost"
                colorScheme="gray"
                color="gray.400"
                isDisabled={hasAnyLoading}
              />
              <IconButton
                as={FaShareAlt}
                p={1}
                boxSize={isChatOpen ? 5 : 6}
                size="xs"
                _hover={{ bg: "gray.100", color: "gray.600" }} 
                cursor="pointer"
                variant="ghost"
                colorScheme="gray"
                color="gray.400"
                aria-label="Share"
                borderRadius="full"
                onClick={handleShare}
              />
            </HStack>
          </HStack>
        )}
      </HStack>
    </Box>
  );
};

export default TreeName;
